import React, { useState } from 'react';
import useSWR from 'swr';
import Swal from 'sweetalert2';
import {
  Alert, Box, Button, Card, CardContent, Chip, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { ambil, kirim, pesanGalat, denganCabang, labelTgl } from './bersama';

// Tab Hari Libur: libur nasional & cuti bersama (superadmin, berlaku semua cabang) dengan pilihan cabang
// libur / tetap buka, dan libur manual cabang.

const LABEL_JENIS = { nasional: 'Libur nasional', cuti_bersama: 'Cuti bersama', cabang: 'Libur cabang' };

const TabHariLibur = ({ cabang, superadmin, onPesan }) => {
  const tahunIni = new Date().getFullYear();
  const [tahun, setTahun] = useState(tahunIni);
  const [baru, setBaru] = useState({ tanggal: '', nama: '', jenis: superadmin ? 'nasional' : 'cabang' });
  const { data, error, mutate } = useSWR(denganCabang('/absensi/hari-libur', cabang, { tahun }), ambil);
  const daftar = data?.data || [];
  const adaCabang = !superadmin || !!cabang;

  const tambah = async () => {
    try {
      const r = await kirim('POST', '/absensi/hari-libur', { ...baru, cabang: cabang || undefined });
      onPesan(r.message); setBaru({ ...baru, tanggal: '', nama: '' }); mutate();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };
  const isiTetap = async () => {
    try {
      const r = await kirim('POST', '/absensi/hari-libur/tanggal-tetap', { tahun });
      Swal.fire('Selesai', r.message, 'success'); mutate();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };
  const hapus = async (l) => {
    const ok = await Swal.fire({ title: `Hapus ${l.nama}?`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Hapus', cancelButtonText: 'Batal' });
    if (!ok.isConfirmed) return;
    try {
      await kirim('DELETE', `/absensi/hari-libur/${l.uuid}`); mutate();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };
  const pilih = async (l, status) => {
    if (!status) return;
    try {
      const r = await kirim('PUT', '/absensi/hari-libur/pilihan', { cabang: cabang || undefined, tanggal: l.tanggal, status });
      onPesan(r.message); mutate();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };
  const bolehHapus = (l) => superadmin || l.jenis === 'cabang';

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Typography fontWeight={700} sx={{ flex: 1 }}>Hari libur {tahun}</Typography>
            <TextField select size="small" value={tahun} onChange={(e) => setTahun(Number(e.target.value))}>
              {[tahunIni - 1, tahunIni, tahunIni + 1, tahunIni + 2].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            {superadmin && <Button size="small" variant="outlined" onClick={isiTetap}>Isi libur bertanggal tetap {tahun}</Button>}
          </Box>
          {superadmin && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Libur nasional & cuti bersama berlaku untuk semua cabang. Tombol isi otomatis hanya untuk libur bertanggal tetap
              (Tahun Baru, Hari Buruh, Pancasila, Kemerdekaan, Natal). Libur lain (Idulfitri, Iduladha, Imlek, Nyepi, Waisak, cuti bersama, dll.)
              isi sesuai SKB 3 Menteri tahun itu.
            </Alert>
          )}
          {!adaCabang && <Alert severity="info" sx={{ mb: 2 }}>Pilih cabang di atas untuk mengatur libur/buka tiap tanggal dan libur cabang.</Alert>}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <TextField type="date" size="small" label="Tanggal" value={baru.tanggal} onChange={(e) => setBaru({ ...baru, tanggal: e.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField size="small" label="Nama" value={baru.nama} onChange={(e) => setBaru({ ...baru, nama: e.target.value })} sx={{ minWidth: 220 }} />
            <TextField select size="small" label="Jenis" value={baru.jenis} onChange={(e) => setBaru({ ...baru, jenis: e.target.value })} sx={{ minWidth: 160 }}>
              {superadmin && <MenuItem value="nasional">Libur nasional</MenuItem>}
              {superadmin && <MenuItem value="cuti_bersama">Cuti bersama</MenuItem>}
              {adaCabang && <MenuItem value="cabang">Libur cabang ini</MenuItem>}
            </TextField>
            <Button variant="contained" onClick={tambah} disabled={!baru.tanggal || !baru.nama}>Tambah</Button>
          </Box>
          {error && <Alert severity="error">{pesanGalat(error)}</Alert>}
          {data && !daftar.length && <Typography color="text.secondary">Belum ada hari libur di tahun ini.</Typography>}
          {daftar.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tanggal</TableCell>
                  <TableCell>Nama</TableCell>
                  <TableCell>Jenis</TableCell>
                  {adaCabang && <TableCell>Cabang</TableCell>}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {daftar.map((l) => (
                  <TableRow key={l.uuid}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{labelTgl(l.tanggal, { weekday: 'short', day: 'numeric', month: 'short' })}</TableCell>
                    <TableCell>{l.nama}</TableCell>
                    <TableCell><Chip size="small" label={LABEL_JENIS[l.jenis]} color={l.jenis === 'cabang' ? 'default' : 'secondary'} /></TableCell>
                    {adaCabang && (
                      <TableCell>
                        {l.jenis === 'cabang' ? 'Libur' : (
                          <ToggleButtonGroup size="small" exclusive value={l.statusCabang} onChange={(_, v) => pilih(l, v)}>
                            <ToggleButton value="libur">Libur</ToggleButton>
                            <ToggleButton value="buka">Tetap buka</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">{bolehHapus(l) && <IconButton size="small" onClick={() => hapus(l)}><Delete fontSize="small" /></IconButton>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
            Libur: karyawan tidak absen tidak dihitung Alfa. Tetap buka: jadwal berjalan biasa. Karyawan yang masuk di libur nasional / cuti bersama
            mendapat tambahan gaji sesuai Aturan.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TabHariLibur;
